// src/contexts/AuthContext.tsx
// src/contexts/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import {
  isNativePlatform,
  signInWithGoogleNative,
  registerNativeGoogleAuthListeners,
} from '@/lib/googleAuthNative';

interface User {
  id: string;
  email: string;
  full_name?: string;
  name?: string;
  tier: string;
  role?: string;
  avatar_url?: string;
  created_at: string;
  is_onboarding_completed?: boolean;
}

export interface SignInResult {
  success: boolean;
  errorCode?: string;
  errorMessage?: string;
}

export interface SignUpResult {
  success: boolean;
  errorCode?: string;
  errorMessage?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  signUp: (email: string, password: string, fullName: string) => Promise<SignUpResult>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  updateUser: (updates: Partial<User>) => Promise<boolean>;
  upgradeTier: (tier: string) => Promise<boolean>;
  completeOnboarding: () => Promise<boolean>;
  refreshUser: () => Promise<void>;
  emergencyLogin: () => Promise<boolean>;
  forceUpdateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const getErrorMessage = (err: any): string => {
  if (typeof err === 'string') return err;
  if (err?.message) return err.message;
  return 'An unexpected error occurred';
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // The onAuthStateChange listener re-announces an already-restored session
  // on subscribe (in addition to the initAuth()/getSession() call below),
  // so we need the latest user via ref, not the state closure, to tell a
  // genuine new sign-in apart from that redundant re-announcement.
  const userRef = useRef<User | null>(null);
  useEffect(() => { userRef.current = user; }, [user]);

  // ─── FIX: use upsert instead of select-then-insert ────────────────────────
  // The old approach caused:
  //   - 400 on SELECT when RLS blocks the query before session attaches
  //   - 409 on INSERT when the row already exists (race condition)
  // upsert with ignoreDuplicates:true handles all cases in one atomic call.
  const ensureWalletExists = useCallback(async (userId: string) => {
    if (!userId) return;
    try {
      const { error } = await supabase
        .from('user_wallets')
        .upsert(
          { user_id: userId, balance: 0 },
          { onConflict: 'user_id', ignoreDuplicates: true }
        );
      if (error) {
        // Log but never throw — wallet creation is non-critical
        console.warn('Wallet upsert warning (non-fatal):', error.message);
      }
    } catch (err) {
      console.warn('Wallet ensure error (non-fatal):', err);
    }
  }, []);

  const fetchOrCreateProfile = useCallback(
    async (userId: string, email: string, fullName?: string): Promise<User | null> => {
      try {
        const { data: existing } = await supabase
          .from('users')
          .select('*')
          .eq('id', userId)
          .maybeSingle();

        if (existing) {
          // Fire-and-forget — don't await, don't block login
          ensureWalletExists(userId).catch(() => {});
          return existing as User;
        }

        const displayName = fullName || email.split('@')[0];
        const { data: newProfile, error: insertError } = await supabase
          .from('users')
          .insert({
            id: userId,
            email,
            full_name: displayName,
            name: displayName,
            tier: 'free',
            role: 'applicant',
            is_onboarding_completed: false,
          })
          .select()
          .single();

        if (insertError) {
          console.error('Profile creation error:', insertError);
          return null;
        }

        // Fire-and-forget wallet creation after profile is created
        ensureWalletExists(userId).catch(() => {});
        return newProfile as User;
      } catch (err) {
        console.error('Profile operation error:', err);
        return null;
      }
    },
    [ensureWalletExists],
  );

  const refreshUser = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user) {
        setUser(null);
        return;
      }

      const profile = await fetchOrCreateProfile(
        session.user.id,
        session.user.email!,
        session.user.user_metadata?.full_name || session.user.user_metadata?.name,
      );

      if (profile) {
        setUser(profile);
        localStorage.setItem('auth_user', JSON.stringify(profile));
        localStorage.setItem('user_tier', profile.tier);
      } else {
        const minimalUser: User = {
          id: session.user.id,
          email: session.user.email!,
          tier: 'free',
          created_at: session.user.created_at,
          is_onboarding_completed: false,
        };
        setUser(minimalUser);
        localStorage.setItem('auth_user', JSON.stringify(minimalUser));
      }
    } catch (err) {
      console.error('Refresh user error:', err);
    }
  }, [fetchOrCreateProfile]);

  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      try {
        // refreshUser() awaits supabase.auth.getSession(), which can hang
        // indefinitely (no network, stalled client) rather than reject —
        // in that case this finally block never runs and isLoading stays
        // true forever, deadlocking every SecureQuantumPage-wrapped route
        // behind "Authenticating...". A bounded timeout guarantees the app
        // always reaches a usable (logged-out) state instead of hanging.
        await Promise.race([
          refreshUser(),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Auth init timed out after 10s')), 10000),
          ),
        ]);
      } catch (err) {
        console.error('Auth init error:', err);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;

        if (event === 'SIGNED_IN' && session?.user) {
          // Same user already loaded (e.g. session restored on page load,
          // then re-announced by this listener) — skip the duplicate
          // fetch; it doubles every downstream context's initial query
          // (AgencyContext -> ClientContext) for no behavioral gain.
          if (userRef.current?.id === session.user.id) {
            if (mounted) setIsLoading(false);
            return;
          }
          const profile = await fetchOrCreateProfile(
            session.user.id,
            session.user.email!,
            session.user.user_metadata?.full_name,
          );
          if (profile && mounted) setUser(profile);
          if (mounted) setIsLoading(false);
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          localStorage.clear();
          if (mounted) setIsLoading(false);
        } else if (event === 'USER_UPDATED') {
          await refreshUser();
          if (mounted) setIsLoading(false);
        }
      },
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [refreshUser, fetchOrCreateProfile]);

  const signIn = useCallback(async (email: string, password: string): Promise<SignInResult> => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      toast.success('Welcome back!');
      // Land on the UPRM. AppLayout redirects to /onboarding if incomplete.
      navigate("/journey");
      return { success: true };
    } catch (err: any) {
      console.error('Sign in error:', err);
      // Login.tsx needs the real error_code (e.g. 'email_not_confirmed') to show
      // an honest message instead of a blanket "Invalid email or password" --
      // that generic text was previously shown for every failure, including
      // unconfirmed accounts, which is what real users were hitting in production.
      return { success: false, errorCode: err?.code, errorMessage: getErrorMessage(err) };
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const signUp = useCallback(
    async (email: string, password: string, fullName: string): Promise<SignUpResult> => {
      setIsLoading(true);
      try {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: { full_name: fullName, name: fullName },
            // Lands on the dedicated confirmation-landing page (ConfirmEmail.tsx),
            // not the generic Google-OAuth /auth/callback -- that page writes to a
            // `profiles` table this app doesn't otherwise use and has unrelated
            // tier-redirect logic from an older flow.
            emailRedirectTo: `${window.location.origin}/confirm-email`,
          },
        });
        if (error) throw error;
        // No toast, no navigate -- Signup.tsx shows a persistent modal instead.
        // The previous toast+navigate('/login') was easy to miss and, combined
        // with Signup.tsx's own subsequent navigate('/onboarding'), produced a
        // confusing double-redirect that dropped unconfirmed users on /login.
        return { success: true };
      } catch (err: any) {
        console.error('Sign up error:', err);
        toast.error(getErrorMessage(err));
        return { success: false, errorCode: err?.code, errorMessage: getErrorMessage(err) };
      } finally {
        setIsLoading(false);
      }
    },
    [navigate],
  );

  const signInWithGoogle = useCallback(async () => {
    setIsLoading(true);
    try {
      if (isNativePlatform()) {
        // Opens the system browser (Chrome Custom Tabs) to the Supabase/Google
        // OAuth flow; the redirect back into the app is caught by the
        // appUrlOpen listener registered in the effect below, which is what
        // actually finishes sign-in and clears isLoading.
        await signInWithGoogleNative();
        return;
      }
      const redirectTo = `${window.location.origin}/auth/callback`;
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo },
      });
      if (error) throw error;
    } catch (err: any) {
      console.error('Google sign in error:', err);
      toast.error(getErrorMessage(err));
      setIsLoading(false);
    }
  }, []);

  // Native-only: catches the Google OAuth redirect back into the app
  // (delivered as an appUrlOpen deep link, not a page load — see
  // src/lib/googleAuthNative.ts for why that requires manual handling
  // instead of relying on supabase-js's automatic detectSessionInUrl).
  useEffect(() => {
    if (!isNativePlatform()) return;

    const unregister = registerNativeGoogleAuthListeners({
      onCallbackHandled: () => {
        // onAuthStateChange (SIGNED_IN, above) picks up the resulting
        // session and fetches/creates the profile on its own; this just
        // stops the spinner and lands the user in the app, matching the
        // web signIn()'s post-login destination.
        setIsLoading(false);
        navigate('/journey');
      },
      onBrowserClosed: () => {
        // Safety net for the user cancelling (closing the tab without
        // completing sign-in) — idempotent if onCallbackHandled already
        // cleared this.
        setIsLoading(false);
      },
    });

    return unregister;
  }, [navigate]);

  const signOut = useCallback(async () => {
    setIsLoading(true);
    try {
      await supabase.auth.signOut();
      localStorage.clear();
      setUser(null);
      toast.success('Signed out successfully');
      navigate('/');
    } catch (err: any) {
      console.error('Sign out error:', err);
      toast.error(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const emergencyLogin = useCallback(async (): Promise<boolean> => {
    setIsLoading(true);
    try {
      const demoEmail = `demo_${Date.now()}@visaguide.com`;
      const demoPassword = 'Demo123!';

      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: demoEmail,
        password: demoPassword,
        options: { data: { full_name: 'Demo User' } },
      });

      if (signUpError) {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: 'demo@visaguide.com',
          password: 'Demo123!',
        });
        if (signInError) throw signInError;
      } else if (signUpData?.user) {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: demoEmail,
          password: demoPassword,
        });
        if (signInError) throw signInError;
      }

      toast.success('Demo access granted!');
      navigate("/journey");
      return true;
    } catch (err: any) {
      console.error('Emergency login error:', err);
      toast.error('Demo login failed. Please sign up manually.');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const updateUser = useCallback(async (updates: Partial<User>): Promise<boolean> => {
    if (!user) return false;
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('users')
        .update({
          full_name: updates.full_name,
          name: updates.name,
          avatar_url: updates.avatar_url,
        })
        .eq('id', user.id);
      if (error) throw error;
      await refreshUser();
      toast.success('Profile updated');
      return true;
    } catch (err: any) {
      console.error('Update user error:', err);
      toast.error(getErrorMessage(err));
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [user, refreshUser]);

  const upgradeTier = useCallback(async (tier: string): Promise<boolean> => {
    if (!user) return false;
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('users')
        .update({ tier })
        .eq('id', user.id);
      if (error) throw error;
      await refreshUser();
      toast.success(`Tier upgraded to ${tier}`);
      return true;
    } catch (err: any) {
      console.error('Upgrade tier error:', err);
      toast.error(getErrorMessage(err));
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [user, refreshUser]);

  const completeOnboarding = useCallback(async (): Promise<boolean> => {
    if (!user) return false;
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('users')
        .update({ is_onboarding_completed: true })
        .eq('id', user.id);
      if (error) throw error;
      await refreshUser();
      toast.success('Onboarding complete!');
      navigate('/assessment-dashboard');
      return true;
    } catch (err: any) {
      console.error('Complete onboarding error:', err);
      toast.error(getErrorMessage(err));
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [user, refreshUser, navigate]);

  const forceUpdateUser = useCallback((newUser: User) => {
    setUser(newUser);
    localStorage.setItem('auth_user', JSON.stringify(newUser));
  }, []);

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    updateUser,
    upgradeTier,
    completeOnboarding,
    refreshUser,
    emergencyLogin,
    forceUpdateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};